import type { CSSProperties } from 'react';

/**
 * Shared desktop text/image split for blocks that place rich text beside an
 * optional image (Click & Expand topics, Comparison Slider panels).
 *
 * Percentages sum to 100% of the content row:
 * - large image: 65% text | 3% gap | 32% image
 * - small image: 75% text | 3% gap | 22% image
 */
export type TextImageSplitSize = 'small' | 'large';

export interface TextImageSplitConfig {
  text: string;
  gap: string;
  image: string;
}

const TEXT_IMAGE_SPLIT_LARGE: TextImageSplitConfig = { text: '65%', gap: '3%', image: '32%' };
const TEXT_IMAGE_SPLIT_SMALL: TextImageSplitConfig = { text: '75%', gap: '3%', image: '22%' };

export function getTextImageSplitConfig(
  size: TextImageSplitSize | undefined,
): TextImageSplitConfig {
  return size === 'large' ? TEXT_IMAGE_SPLIT_LARGE : TEXT_IMAGE_SPLIT_SMALL;
}

/**
 * Desktop grid layout for the text/image split. Spread over the block's base
 * row style when an image is present.
 */
export function getTextImageSplitGridStyle(
  size: TextImageSplitSize | undefined,
): CSSProperties {
  const config = getTextImageSplitConfig(size);

  return {
    display: 'grid',
    gridTemplateColumns: `minmax(0, ${config.text}) minmax(0, ${config.image})`,
    columnGap: config.gap,
    alignItems: 'start',
  };
}

/**
 * Mobile override for the image in a text/image split: the image drops below
 * the text and is centered at min(60%, 240px). Sites keep their own
 * `maxWidth` (they differ today and are preserved as-is).
 */
export const textImageSplitMobileImageStyle: CSSProperties = {
  width: 'min(60%, 240px)',
  alignSelf: 'center',
};
